package dev.wyns.ventrova.ui.map

import dev.wyns.ventrova.app.PlaceItem
import dev.wyns.ventrova.data.model.OpenStatus
import kotlin.math.abs
import kotlin.math.hypot

/** A marker's footprint on screen, in dp from the map's top left; [centerY] is its anchor. */
internal data class MarkerBox(
    val id: String,
    val centerX: Float,
    val centerY: Float,
    val width: Float,
    val height: Float,
)

/** Result of [declutterMarkers]. */
internal data class MarkerLayout(
    /** Every on-screen place as it would be drawn as a marker, in priority order. */
    val boxes: List<MarkerBox>,
    /** Places drawn as a full marker. */
    val markers: Set<String>,
    /** Places drawn as a dot because a neighbor's marker takes their spot. */
    val hiddenByNeighbors: Set<String>,
) {
    /** Topmost full marker under ([x], [y]). */
    fun markerAt(x: Float, y: Float): MarkerBox? = boxes.firstOrNull { it.id in markers && it.contains(x, y) }

    /** The dot nearest to ([x], [y]) within [radius], unless a full marker covers that point. */
    fun dotAt(x: Float, y: Float, radius: Float, onlyHidden: Boolean = false): MarkerBox? {
        if (markerAt(x, y) != null) return null
        return boxes
            .filter { it.id !in markers && (!onlyHidden || it.id in hiddenByNeighbors) }
            .map { it to hypot(it.centerX - x, it.centerY - y) }
            .filter { (_, distance) -> distance <= radius }
            .minByOrNull { (_, distance) -> distance }
            ?.first
    }

    companion object {
        val Empty = MarkerLayout(emptyList(), emptySet(), emptySet())
    }
}

/**
 * Which places get a full marker: walks [boxes] in priority order and keeps each one that doesn't
 * overlap a kept marker by more than [overlapAllowance] dp, up to [maxMarkers]. The others are
 * drawn as dots; those that lost their spot to a neighbor are [MarkerLayout.hiddenByNeighbors].
 */
internal fun declutterMarkers(boxes: List<MarkerBox>, maxMarkers: Int, overlapAllowance: Float = 4f): MarkerLayout {
    val kept = ArrayList<MarkerBox>(minOf(boxes.size, maxMarkers))
    val hidden = HashSet<String>()
    for (box in boxes) {
        if (kept.size >= maxMarkers) break
        if (kept.none { it.overlaps(box, overlapAllowance) }) kept += box else hidden += box.id
    }
    return MarkerLayout(boxes, kept.mapTo(HashSet()) { it.id }, hidden)
}

internal fun MarkerBox.contains(x: Float, y: Float): Boolean =
    abs(centerX - x) <= width / 2 && abs(centerY - y) <= height / 2

private fun MarkerBox.overlaps(other: MarkerBox, allowance: Float): Boolean {
    val dx = abs(centerX - other.centerX)
    val dy = abs(centerY - other.centerY)
    return dx < (width + other.width) / 2 - allowance && dy < (height + other.height) / 2 - allowance
}

/**
 * Order in which places claim a full marker: the selection, then the most plant-based, then open
 * before unknown hours before closed, then the nearest.
 */
internal fun markerPriority(selectedId: String?): Comparator<PlaceItem> =
    compareBy<PlaceItem>(
        { it.id != selectedId },
        { it.place.veganLevel.ordinal },
        {
            when (it.openStatus) {
                null -> 1
                is OpenStatus.Closed -> 2
                else -> 0
            }
        },
        { it.distanceMeters ?: Double.MAX_VALUE },
    )
